import React, { useState, useEffect } from 'react';
import { Layout } from '@layout/Layout';
import { Calendar, Users, Sparkles, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, RefreshCw, Plus, Upload, Trash2, Pencil, Download } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import useHRHolidayStore from '@/store/HRHolidayStore';
import './HolidayManagement.css';

interface EmployeeSelection {
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    selected_holidays: Array<{
        holiday_id: number;
        holiday_name: string;
        holiday_date: string;
        selected_at: string;
    }>;
}

const HolidayManagement: React.FC = () => {
    const { isDarkMode } = useTheme();
    const {
        publicHolidays,
        allHolidays,
        employeeSelections,
        optionalHolidays,
        loading,
        selectedYear,
        setSelectedYear,
        fetchPublicHolidays,
        fetchAllHolidays,
        fetchAllEmployeeSelections,
        fetchOptionalHolidays,
        cancelEmployeeOptionalHolidays,
        replaceEmployeeOptionalHolidays,
        bulkAddHolidays,
        addNewHoliday,
        deleteExistingHoliday,
        updateExistingHoliday
    } = useHRHolidayStore();

    const [searchTerm, setSearchTerm] = useState('');
    const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
    const [filterView, setFilterView] = useState<'all' | 'selected' | 'notSelected'>('all');
    const [currentPage, setCurrentPage] = useState(1);

    // Edit holiday state
    const [showEditHolidayModal, setShowEditHolidayModal] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState<any>(null);
    const [editHolidayName, setEditHolidayName] = useState('');
    const [editHolidayDate, setEditHolidayDate] = useState('');
    const [editHolidayOptional, setEditHolidayOptional] = useState(false);
    const [editHolidayLoading, setEditHolidayLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelingEmployee, setCancelingEmployee] = useState<EmployeeSelection | null>(null);
    const [selectedHolidaysToCancel, setSelectedHolidaysToCancel] = useState<number[]>([]);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [replacingEmployee, setReplacingEmployee] = useState<EmployeeSelection | null>(null);
    const [holidaysToReplace, setHolidaysToReplace] = useState<{[key: number]: number}>({});
    const [replaceLoading, setReplaceLoading] = useState(false);
    const [replaceError, setReplaceError] = useState<string | null>(null);

    // Bulk Add Holidays state
    const [showBulkAddModal, setShowBulkAddModal] = useState(false);
    const [bulkHolidayRows, setBulkHolidayRows] = useState<Array<{holiday_name: string, holiday_date: string, is_optional: boolean}>>([
        { holiday_name: '', holiday_date: '', is_optional: false }
    ]);
    const [bulkAddLoading, setBulkAddLoading] = useState(false);
    const [bulkAddError, setBulkAddError] = useState<string | null>(null);
    const [bulkAddSuccess, setBulkAddSuccess] = useState<string | null>(null);

    // Year selector state
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const defaultFYYear = currentMonth < 3 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
    const [displayYear, setDisplayYear] = useState(selectedYear || defaultFYYear);

    const itemsPerPage = 15;
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const loadData = async () => {
            try {
                await Promise.all([
                    fetchPublicHolidays(displayYear),
                    fetchAllEmployeeSelections(displayYear),
                    fetchOptionalHolidays(),
                    fetchAllHolidays(displayYear)
                ]);
            } catch (error) {
                console.error('Failed to load holiday data:', error);
            }
        };

        loadData();
    }, []);

    // Reload all year-dependent data when year changes
    useEffect(() => {
        const reloadDataForYear = async () => {
            try {
                await Promise.all([
                    fetchPublicHolidays(displayYear),
                    fetchAllEmployeeSelections(displayYear),
                    fetchAllHolidays(displayYear)
                ]);
            } catch (error) {
                console.error('Failed to reload data for year:', error);
            }
        };

        reloadDataForYear();
    }, [displayYear]);

    // Year change handler
    const handleYearChange = (newYear: number) => {
        setDisplayYear(newYear);
        setSelectedYear(newYear);
    };

    // Generate year options (current - 5 to current + 5)
    const yearOptions = Array.from({ length: 11 }, (_, i) => defaultFYYear - 5 + i);

    // Bulk add handlers
    const handleAddBulkRow = () => {
        setBulkHolidayRows([...bulkHolidayRows, { holiday_name: '', holiday_date: '', is_optional: false }]);
    };

    const handleRemoveBulkRow = (index: number) => {
        if (bulkHolidayRows.length > 1) {
            setBulkHolidayRows(bulkHolidayRows.filter((_, i) => i !== index));
        }
    };

    const handleBulkRowChange = (index: number, field: string, value: any) => {
        const updated = [...bulkHolidayRows];
        (updated[index] as any)[field] = value;
        setBulkHolidayRows(updated);
    };

    const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());
            // Skip header row
            const dataLines = lines.slice(1);
            const parsed = dataLines.map(line => {
                const parts = line.split(',').map(p => p.trim().replace(/^"|"$/g, ''));
                return {
                    holiday_name: parts[0] || '',
                    holiday_date: parts[1] || '',
                    is_optional: parts[2]?.toLowerCase() === 'true' || parts[2] === '1'
                };
            }).filter(row => row.holiday_name && row.holiday_date);

            if (parsed.length > 0) {
                setBulkHolidayRows(parsed);
            }
        };
        reader.readAsText(file);
        // Reset input
        event.target.value = '';
    };

    const handleSubmitBulkAdd = async () => {
        const validRows = bulkHolidayRows.filter(r => r.holiday_name && r.holiday_date);
        if (validRows.length === 0) {
            setBulkAddError('Please add at least one valid holiday entry');
            return;
        }

        setBulkAddLoading(true);
        setBulkAddError(null);
        setBulkAddSuccess(null);

        try {
            const result = await bulkAddHolidays(validRows);
            if (result.success) {
                const data = result.data;
                setBulkAddSuccess(`Added ${data.insertedCount} holiday(s)${data.skippedCount > 0 ? `, skipped ${data.skippedCount} duplicate(s)` : ''}`);
                setBulkHolidayRows([{ holiday_name: '', holiday_date: '', is_optional: false }]);
                // Refresh holidays for the current display year
                fetchAllHolidays(displayYear);
            } else {
                setBulkAddError(result.error || 'Failed to add holidays');
            }
        } catch (error: any) {
            setBulkAddError(error.message || 'Failed to add holidays');
        } finally {
            setBulkAddLoading(false);
        }
    };

    const toggleEmployeeExpand = (userId: number) => {
        const newExpanded = new Set(expandedEmployees);
        if (newExpanded.has(userId)) {
            newExpanded.delete(userId);
        } else {
            newExpanded.add(userId);
        }
        setExpandedEmployees(newExpanded);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const filteredEmployees = employeeSelections?.filter((emp: EmployeeSelection) => {
        const matchesSearch =
            emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase());

        if (filterView === 'selected') {
            return matchesSearch && emp.selected_holidays.length > 0;
        } else if (filterView === 'notSelected') {
            return matchesSearch && emp.selected_holidays.length === 0;
        }

        return matchesSearch;
    }) || [];

    // Pagination logic
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

    // Reset to page 1 when filter or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterView]);

    const totalEmployees = employeeSelections?.length || 0;
    const employeesWithSelections = employeeSelections?.filter((emp: EmployeeSelection) => emp.selected_holidays.length > 0).length || 0;
    const employeesWithoutSelections = totalEmployees - employeesWithSelections;

    const formatRole = (role: string) => {
        return role
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Download sample CSV
    const handleDownloadSampleCSV = () => {
        const csvContent = 'holiday_name,holiday_date,is_optional\nRepublic Day,2026-01-26,false\nDiwali,2025-10-20,true\nChristmas,2025-12-25,false';
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'holidays_sample.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    // Edit holiday handlers
    const handleOpenEditHoliday = (holiday: any) => {
        setEditingHoliday(holiday);
        setEditHolidayName(holiday.holiday_name);
        setEditHolidayDate(holiday.holiday_date.split('T')[0]);
        setEditHolidayOptional(holiday.is_optional);
        setShowEditHolidayModal(true);
    };

    const handleSubmitEditHoliday = async () => {
        if (!editingHoliday || !editHolidayName || !editHolidayDate) return;
        setEditHolidayLoading(true);
        try {
            const result = await updateExistingHoliday(editingHoliday.id, {
                holiday_name: editHolidayName,
                holiday_date: editHolidayDate,
                is_optional: editHolidayOptional
            });
            if (result.success) {
                setShowEditHolidayModal(false);
                setEditingHoliday(null);
                fetchAllHolidays(displayYear);
            }
        } catch (error) {
            console.error('Edit holiday error:', error);
        } finally {
            setEditHolidayLoading(false);
        }
    };

    const handleDeleteHoliday = async (holidayId: number, holidayName: string) => {
        if (!window.confirm(`Are you sure you want to delete "${holidayName}"?`)) return;
        try {
            await deleteExistingHoliday(holidayId);
            fetchAllHolidays(displayYear);
        } catch (error) {
            console.error('Delete holiday error:', error);
        }
    };

    const handleOpenCancelModal = (employee: EmployeeSelection) => {
        setCancelingEmployee(employee);
        setSelectedHolidaysToCancel([]);
        setCancelError(null);
        setShowCancelModal(true);
    };

    const handleCloseCancelModal = () => {
        setShowCancelModal(false);
        setCancelingEmployee(null);
        setSelectedHolidaysToCancel([]);
        setCancelError(null);
    };

    const toggleHolidayToCancel = (holidayId: number) => {
        setSelectedHolidaysToCancel(prev =>
            prev.includes(holidayId)
                ? prev.filter(id => id !== holidayId)
                : [...prev, holidayId]
        );
    };

    const handleSubmitCancel = async () => {
        if (!cancelingEmployee) {
            setCancelError('No employee selected.');
            return;
        }

        if (selectedHolidaysToCancel.length === 0) {
            setCancelError('Please select at least one holiday to cancel.');
            return;
        }

        try {
            setCancelLoading(true);
            setCancelError(null);

            const result = await cancelEmployeeOptionalHolidays(
                cancelingEmployee.user_id,
                selectedHolidaysToCancel
            );

            if (result.success) {
                const { cancelledCount } = result.data;
                alert(`Successfully cancelled ${cancelledCount} holiday(s) for ${cancelingEmployee.first_name} ${cancelingEmployee.last_name}`);
                handleCloseCancelModal();
            } else {
                setCancelError(result.error || 'Failed to cancel holidays');
            }
        } catch (error: any) {
            setCancelError(error.message || 'An error occurred while canceling the holidays');
        } finally {
            setCancelLoading(false);
        }
    };

    // Replace holiday handlers
    const handleOpenReplaceModal = (employee: EmployeeSelection) => {
        setReplacingEmployee(employee);
        setHolidaysToReplace({});
        setReplaceError(null);
        setShowReplaceModal(true);
    };

    const handleCloseReplaceModal = () => {
        setShowReplaceModal(false);
        setReplacingEmployee(null);
        setHolidaysToReplace({});
        setReplaceError(null);
    };

    const handleReplaceHolidaySelection = (oldHolidayId: number, newHolidayId: number) => {
        setHolidaysToReplace(prev => ({
            ...prev,
            [oldHolidayId]: newHolidayId
        }));
    };

    const getAvailableReplacementHolidays = () => {
        if (!replacingEmployee || !optionalHolidays) return [];

        const selectedHolidayIds = replacingEmployee.selected_holidays.map(h => h.holiday_id);
        const alreadyReplacedIds = Object.values(holidaysToReplace);

        return optionalHolidays.filter((holiday: any) =>
            !selectedHolidayIds.includes(holiday.id) && !alreadyReplacedIds.includes(holiday.id)
        );
    };

    const handleSubmitReplace = async () => {
        if (!replacingEmployee) {
            setReplaceError('No employee selected.');
            return;
        }

        const cancelIds = Object.keys(holidaysToReplace).map(Number);
        const addIds = Object.values(holidaysToReplace);

        if (cancelIds.length === 0) {
            setReplaceError('Please select at least one holiday to replace.');
            return;
        }

        try {
            setReplaceLoading(true);
            setReplaceError(null);

            const result = await replaceEmployeeOptionalHolidays(
                replacingEmployee.user_id,
                cancelIds,
                addIds
            );

            if (result.success) {
                const { summary } = result.data;
                alert(`Successfully replaced ${summary.cancelled_count} holiday(s) for ${replacingEmployee.first_name} ${replacingEmployee.last_name}`);
                handleCloseReplaceModal();
            } else {
                setReplaceError(result.error || 'Failed to replace holidays');
            }
        } catch (error: any) {
            setReplaceError(error.message || 'An error occurred while replacing the holidays');
        } finally {
            setReplaceLoading(false);
        }
    };

    return (
        <Layout>
            <div className="holiday-management-container">
                {/* Header */}
                <div className="management-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <Calendar size={48} />
                        </div>
                        <div className="header-text">
                            <h1 className="header-title">Holiday Management</h1>
                            <p className="header-subtitle">
                                View and manage employee holiday selections
                            </p>
                        </div>
                    </div>

                    {/* Year Selector & Bulk Add */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <label style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isDarkMode ? '#d1d5db' : '#374151'
                            }}>
                                Financial Year:
                            </label>
                            <select
                                value={displayYear}
                                onChange={(e) => handleYearChange(Number(e.target.value))}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                    color: isDarkMode ? '#f3f4f6' : '#111827',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    minWidth: '160px'
                                }}
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>
                                        {year} - {year + 1}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={() => {
                                setBulkAddError(null);
                                setBulkAddSuccess(null);
                                setBulkHolidayRows([{ holiday_name: '', holiday_date: '', is_optional: false }]);
                                setShowBulkAddModal(true);
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                backgroundColor: isDarkMode ? '#059669' : '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#047857' : '#059669'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#059669' : '#10b981'}
                        >
                            <Plus size={18} />
                            Bulk Add Holidays
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon public">
                                <Calendar size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{publicHolidays?.length || 0}</div>
                                <div className="stat-label">Public Holidays</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon users">
                                <Users size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{totalEmployees}</div>
                                <div className="stat-label">Total Users</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon selected">
                                <Sparkles size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{employeesWithSelections}</div>
                                <div className="stat-label">Selections Made</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Public Holidays Section */}
                <section className="public-holidays-section">
                    <div className="section-header">
                        <h2 className="section-title">
                            <Calendar className="section-icon" size={24} />
                            Public Holidays ({displayYear} - {displayYear + 1})
                        </h2>
                        <span style={{
                            fontSize: '14px',
                            color: isDarkMode ? '#9ca3af' : '#6b7280',
                            fontWeight: '400'
                        }}>
                            {publicHolidays?.length || 0} public holidays
                        </span>
                    </div>
                    <div className="holidays-carousel">
                        {(!publicHolidays || publicHolidays.length === 0) ? (
                            <div className="empty-message">No public holidays found for FY {displayYear}-{displayYear + 1}</div>
                        ) : (
                            publicHolidays.map((holiday: any) => (
                                <div key={holiday.id} className="holiday-chip">
                                    <div className="chip-date">{formatDate(holiday.holiday_date)}</div>
                                    <div className="chip-name">{holiday.holiday_name}</div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* All Holidays for Selected Year */}
                <section className="public-holidays-section">
                    <div className="section-header">
                        <h2 className="section-title">
                            <Calendar className="section-icon" size={24} />
                            All Holidays ({displayYear} - {displayYear + 1})
                        </h2>
                        <span style={{
                            fontSize: '14px',
                            color: isDarkMode ? '#9ca3af' : '#6b7280',
                            fontWeight: '400'
                        }}>
                            {allHolidays?.length || 0} holidays
                        </span>
                    </div>
                    <div className="holidays-carousel" style={{ flexWrap: 'wrap' }}>
                        {(!allHolidays || allHolidays.length === 0) ? (
                            <div className="empty-message">No holidays found for FY {displayYear}-{displayYear + 1}</div>
                        ) : (
                            allHolidays.map((holiday: any) => (
                                <div key={holiday.id} className="holiday-chip" style={{
                                    borderLeft: `3px solid ${holiday.is_optional ? '#f59e0b' : '#3b82f6'}`,
                                    position: 'relative'
                                }}>
                                    <div className="chip-date">{formatDate(holiday.holiday_date)}</div>
                                    <div className="chip-name">{holiday.holiday_name}</div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginTop: '2px'
                                    }}>
                                        <span style={{
                                            fontSize: '11px',
                                            color: holiday.is_optional ? '#f59e0b' : '#3b82f6',
                                            fontWeight: '600'
                                        }}>
                                            {holiday.is_optional ? 'Optional' : 'Public'}
                                        </span>
                                        <span style={{ display: 'flex', gap: '4px' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleOpenEditHoliday(holiday); }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '2px',
                                                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                                                    borderRadius: '4px'
                                                }}
                                                title="Edit"
                                            >
                                                <Pencil size={13} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteHoliday(holiday.id, holiday.holiday_name); }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '2px',
                                                    color: '#ef4444',
                                                    borderRadius: '4px'
                                                }}
                                                title="Delete"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </section>

                {/* Employee Selections Section */}
                <section className="employee-selections-section">
                    <div className="section-header">
                        <h2 className="section-title">
                            <Users className="section-icon" size={28} />
                            User Holiday Selections ({displayYear} - {displayYear + 1})
                        </h2>
                    </div>

                    {/* Filter and Search Controls Below Title */}
                    <div className="filter-controls">
                        {/* Search */}
                        <div className="search-box">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Filter */}
                        <div className="filter-group">
                            <button
                                className={`filter-btn ${filterView === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterView('all')}
                            >
                                All ({totalEmployees})
                            </button>
                            <button
                                className={`filter-btn ${filterView === 'selected' ? 'active' : ''}`}
                                onClick={() => setFilterView('selected')}
                            >
                                Selected ({employeesWithSelections})
                            </button>
                            <button
                                className={`filter-btn ${filterView === 'notSelected' ? 'active' : ''}`}
                                onClick={() => setFilterView('notSelected')}
                            >
                                Pending ({employeesWithoutSelections})
                            </button>
                        </div>
                    </div>

                    {/* Employee List */}
                    <div className="employee-list">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading employee data...</p>
                            </div>
                        ) : filteredEmployees.length === 0 ? (
                            <div className="empty-state">
                                <Users size={64} />
                                <h3>No users found</h3>
                                <p>Try adjusting your search or filter criteria</p>
                            </div>
                        ) : (
                            paginatedEmployees.map((employee: EmployeeSelection) => (
                                <div
                                    key={employee.user_id}
                                    className={`employee-card ${expandedEmployees.has(employee.user_id) ? 'expanded' : ''}`}
                                >
                                    <div
                                        className="employee-header"
                                        onClick={() => toggleEmployeeExpand(employee.user_id)}
                                    >
                                        <div className="employee-info">
                                            <div className="employee-avatar">
                                                {employee.first_name[0]}{employee.last_name[0]}
                                            </div>
                                            <div className="employee-details">
                                                <div className="employee-name">
                                                    {employee.first_name} {employee.last_name}
                                                </div>
                                                <div className="employee-meta">
                                                    <span className="employee-email">{employee.email}</span>
                                                    <span className="employee-role-badge">{formatRole(employee.role)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="employee-summary">
                                            <div className="selection-badge">
                                                {employee.selected_holidays.length > 0 ? (
                                                    <>
                                                        <Sparkles size={16} />
                                                        <span>{employee.selected_holidays.length} Selected</span>
                                                    </>
                                                ) : (
                                                    <span className="no-selection">Not selected yet</span>
                                                )}
                                            </div>
                                            <button className="expand-btn">
                                                {expandedEmployees.has(employee.user_id) ? (
                                                    <ChevronUp size={20} />
                                                ) : (
                                                    <ChevronDown size={20} />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Show holiday selections when expanded OR show "Not selected yet" message */}
                                    {expandedEmployees.has(employee.user_id) && (
                                        <div className="employee-holidays">
                                            {employee.selected_holidays.length > 0 ? (
                                                <>
                                                    <div className="holidays-grid">
                                                        {employee.selected_holidays.map((holiday, index) => (
                                                            <div key={holiday.holiday_id} className="holiday-item">
                                                                <div className="holiday-number">{index + 1}</div>
                                                                <div className="holiday-details">
                                                                    <div className="holiday-name">{holiday.holiday_name}</div>
                                                                    <div className="holiday-date">{formatDate(holiday.holiday_date)}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div style={{ padding: '10px 20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                                        <button
                                                            onClick={() => handleOpenReplaceModal(employee)}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '8px 16px',
                                                                backgroundColor: isDarkMode ? '#0891b2' : '#06b6d4',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#0e7490' : '#0891b2'}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#0891b2' : '#06b6d4'}
                                                        >
                                                            <RefreshCw size={16} />
                                                            Replace Holidays
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenCancelModal(employee)}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '8px 16px',
                                                                backgroundColor: isDarkMode ? '#dc2626' : '#ef4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                fontWeight: '500',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#b91c1c' : '#dc2626'}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#dc2626' : '#ef4444'}
                                                        >
                                                            <X size={16} />
                                                            Cancel Holidays
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div style={{
                                                    padding: '20px',
                                                    textAlign: 'center',
                                                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                                                    fontSize: '14px',
                                                    fontStyle: 'italic'
                                                }}>
                                                    This user has not selected any optional holidays yet.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {!loading && filteredEmployees.length > 0 && (
                        <div className="pagination-container">
                            <div className="pagination-info">
                                Showing {startIndex + 1} to {Math.min(endIndex, filteredEmployees.length)} of {filteredEmployees.length} users
                            </div>
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                    Previous
                                </button>
                                <div className="pagination-pages">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* Cancel Holiday Modal */}
                {showCancelModal && cancelingEmployee && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '20px',
                                borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <h2 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: isDarkMode ? '#f3f4f6' : '#111827'
                                }}>
                                    Cancel Holidays for {cancelingEmployee.first_name} {cancelingEmployee.last_name}
                                </h2>
                                <button
                                    onClick={handleCloseCancelModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        color: isDarkMode ? '#9ca3af' : '#6b7280'
                                    }}
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={{
                                padding: '20px',
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0
                            }}>
                                {cancelError && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '20px',
                                        backgroundColor: '#fee2e2',
                                        color: '#991b1b',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}>
                                        {cancelError}
                                    </div>
                                )}

                                {/* Select holidays to cancel */}
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: isDarkMode ? '#f3f4f6' : '#111827'
                                    }}>
                                        Select holidays to cancel ({selectedHolidaysToCancel.length} selected)
                                    </label>
                                    {cancelingEmployee.selected_holidays.length === 0 ? (
                                        <div style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: isDarkMode ? '#9ca3af' : '#6b7280',
                                            fontSize: '14px',
                                            fontStyle: 'italic',
                                            backgroundColor: isDarkMode ? '#111827' : '#f9fafb',
                                            borderRadius: '8px'
                                        }}>
                                            This employee has no selected holidays to cancel.
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'grid',
                                            gap: '8px'
                                        }}>
                                            {cancelingEmployee.selected_holidays.map((holiday) => (
                                                <div
                                                    key={holiday.holiday_id}
                                                    onClick={() => toggleHolidayToCancel(holiday.holiday_id)}
                                                    style={{
                                                        padding: '12px',
                                                        border: `2px solid ${selectedHolidaysToCancel.includes(holiday.holiday_id) ? '#dc2626' : (isDarkMode ? '#374151' : '#e5e7eb')}`,
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedHolidaysToCancel.includes(holiday.holiday_id) ? (isDarkMode ? '#7f1d1d' : '#fee2e2') : (isDarkMode ? '#111827' : '#f9fafb'),
                                                        transition: 'all 0.2s ease',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedHolidaysToCancel.includes(holiday.holiday_id)}
                                                        onChange={() => toggleHolidayToCancel(holiday.holiday_id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: '18px',
                                                            height: '18px',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontWeight: '500',
                                                            color: isDarkMode ? '#f3f4f6' : '#111827',
                                                            marginBottom: '4px'
                                                        }}>
                                                            {holiday.holiday_name}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '14px',
                                                            color: isDarkMode ? '#9ca3af' : '#6b7280'
                                                        }}>
                                                            {formatDate(holiday.holiday_date)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '20px',
                                borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                flexShrink: 0
                            }}>
                                <button
                                    onClick={handleCloseCancelModal}
                                    disabled={cancelLoading}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                                        color: isDarkMode ? '#f3f4f6' : '#111827',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: cancelLoading ? 'not-allowed' : 'pointer',
                                        opacity: cancelLoading ? 0.5 : 1
                                    }}
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleSubmitCancel}
                                    disabled={cancelLoading || selectedHolidaysToCancel.length === 0}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: (selectedHolidaysToCancel.length === 0 || cancelLoading) ? '#9ca3af' : (isDarkMode ? '#dc2626' : '#ef4444'),
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: (selectedHolidaysToCancel.length === 0 || cancelLoading) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {cancelLoading ? 'Cancelling...' : 'Cancel Holidays'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Replace Holiday Modal */}
                {showReplaceModal && replacingEmployee && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '700px',
                            width: '100%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '20px',
                                borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <h2 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: isDarkMode ? '#f3f4f6' : '#111827'
                                }}>
                                    Replace Holidays for {replacingEmployee.first_name} {replacingEmployee.last_name}
                                </h2>
                                <button
                                    onClick={handleCloseReplaceModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        color: isDarkMode ? '#9ca3af' : '#6b7280'
                                    }}
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={{
                                padding: '20px',
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0
                            }}>
                                {replaceError && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '20px',
                                        backgroundColor: '#fee2e2',
                                        color: '#991b1b',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}>
                                        {replaceError}
                                    </div>
                                )}

                                <div style={{
                                    padding: '12px',
                                    marginBottom: '20px',
                                    backgroundColor: isDarkMode ? '#1e3a8a' : '#dbeafe',
                                    color: isDarkMode ? '#bfdbfe' : '#1e40af',
                                    borderRadius: '8px',
                                    fontSize: '14px'
                                }}>
                                    Select a current holiday and choose which holiday to replace it with. You must replace equal number of holidays.
                                </div>

                                {/* Select holidays to replace */}
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '12px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: isDarkMode ? '#f3f4f6' : '#111827'
                                    }}>
                                        Current Holidays ({Object.keys(holidaysToReplace).length} selected to replace)
                                    </label>
                                    {replacingEmployee.selected_holidays.length === 0 ? (
                                        <div style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: isDarkMode ? '#9ca3af' : '#6b7280',
                                            fontSize: '14px',
                                            fontStyle: 'italic',
                                            backgroundColor: isDarkMode ? '#111827' : '#f9fafb',
                                            borderRadius: '8px'
                                        }}>
                                            This employee has no selected holidays to replace.
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'grid',
                                            gap: '12px'
                                        }}>
                                            {replacingEmployee.selected_holidays.map((holiday) => (
                                                <div
                                                    key={holiday.holiday_id}
                                                    style={{
                                                        padding: '16px',
                                                        border: `2px solid ${holidaysToReplace[holiday.holiday_id] ? '#0891b2' : (isDarkMode ? '#374151' : '#e5e7eb')}`,
                                                        borderRadius: '8px',
                                                        backgroundColor: holidaysToReplace[holiday.holiday_id] ? (isDarkMode ? '#164e63' : '#cffafe') : (isDarkMode ? '#111827' : '#f9fafb'),
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '12px'
                                                    }}>
                                                        <div>
                                                            <div style={{
                                                                fontWeight: '600',
                                                                color: isDarkMode ? '#f3f4f6' : '#111827',
                                                                marginBottom: '4px'
                                                            }}>
                                                                {holiday.holiday_name}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '14px',
                                                                color: isDarkMode ? '#9ca3af' : '#6b7280'
                                                            }}>
                                                                {formatDate(holiday.holiday_date)}
                                                            </div>
                                                        </div>
                                                        <RefreshCw size={18} color={isDarkMode ? '#06b6d4' : '#0891b2'} />
                                                    </div>

                                                    <select
                                                        value={holidaysToReplace[holiday.holiday_id] || ''}
                                                        onChange={(e) => handleReplaceHolidaySelection(holiday.holiday_id, Number(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            borderRadius: '6px',
                                                            border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
                                                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                                            color: isDarkMode ? '#f3f4f6' : '#111827',
                                                            fontSize: '14px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value="">Select replacement holiday...</option>
                                                        {getAvailableReplacementHolidays().map((availHoliday: any) => (
                                                            <option key={availHoliday.id} value={availHoliday.id}>
                                                                {availHoliday.holiday_name} - {formatDate(availHoliday.holiday_date)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '20px',
                                borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px',
                                flexShrink: 0
                            }}>
                                <button
                                    onClick={handleCloseReplaceModal}
                                    disabled={replaceLoading}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                                        color: isDarkMode ? '#f3f4f6' : '#111827',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: replaceLoading ? 'not-allowed' : 'pointer',
                                        opacity: replaceLoading ? 0.5 : 1
                                    }}
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleSubmitReplace}
                                    disabled={replaceLoading || Object.keys(holidaysToReplace).length === 0}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: (Object.keys(holidaysToReplace).length === 0 || replaceLoading) ? '#9ca3af' : (isDarkMode ? '#0891b2' : '#06b6d4'),
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        cursor: (Object.keys(holidaysToReplace).length === 0 || replaceLoading) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {replaceLoading ? 'Replacing...' : 'Replace Holidays'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Bulk Add Holidays Modal */}
                {showBulkAddModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-end',
                        zIndex: 1000,
                        padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                            borderRadius: '12px',
                            maxWidth: '800px',
                            width: '100%',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '20px',
                                borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <h2 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: isDarkMode ? '#f3f4f6' : '#111827'
                                }}>
                                    Bulk Add Holidays
                                </h2>
                                <button
                                    onClick={() => setShowBulkAddModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        color: isDarkMode ? '#9ca3af' : '#6b7280'
                                    }}
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div style={{
                                padding: '20px',
                                flex: 1,
                                overflowY: 'auto',
                                minHeight: 0
                            }}>
                                {bulkAddError && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '16px',
                                        backgroundColor: '#fee2e2',
                                        color: '#991b1b',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}>
                                        {bulkAddError}
                                    </div>
                                )}

                                {bulkAddSuccess && (
                                    <div style={{
                                        padding: '12px',
                                        marginBottom: '16px',
                                        backgroundColor: isDarkMode ? '#064e3b' : '#d1fae5',
                                        color: isDarkMode ? '#a7f3d0' : '#065f46',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}>
                                        {bulkAddSuccess}
                                    </div>
                                )}

                                {/* CSV Import */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{
                                        fontSize: '13px',
                                        color: isDarkMode ? '#9ca3af' : '#6b7280'
                                    }}>
                                        CSV format: holiday_name, holiday_date (YYYY-MM-DD), is_optional (true/false)
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={handleDownloadSampleCSV}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '6px 14px',
                                                backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                                                color: isDarkMode ? '#d1d5db' : '#374151',
                                                border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <Download size={14} />
                                            Sample CSV
                                        </button>
                                        <label style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 14px',
                                            backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                                            color: isDarkMode ? '#d1d5db' : '#374151',
                                            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}>
                                            <Upload size={14} />
                                            Import CSV
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleCSVImport}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>
                                </div>

                                {/* Holiday Rows Table */}
                                <div style={{
                                    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    {/* Table Header */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 180px 120px 50px',
                                        gap: '12px',
                                        padding: '12px 16px',
                                        backgroundColor: isDarkMode ? '#111827' : '#f9fafb',
                                        borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: isDarkMode ? '#d1d5db' : '#374151'
                                    }}>
                                        <div>Holiday Name</div>
                                        <div>Date</div>
                                        <div>Type</div>
                                        <div></div>
                                    </div>

                                    {/* Table Rows */}
                                    {bulkHolidayRows.map((row, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 180px 120px 50px',
                                                gap: '12px',
                                                padding: '10px 16px',
                                                borderBottom: index < bulkHolidayRows.length - 1
                                                    ? `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`
                                                    : 'none',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Holiday name"
                                                value={row.holiday_name}
                                                onChange={(e) => handleBulkRowChange(index, 'holiday_name', e.target.value)}
                                                style={{
                                                    padding: '8px 10px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                                    color: isDarkMode ? '#f3f4f6' : '#111827',
                                                    fontSize: '14px',
                                                    width: '100%'
                                                }}
                                            />
                                            <input
                                                type="date"
                                                value={row.holiday_date}
                                                onChange={(e) => handleBulkRowChange(index, 'holiday_date', e.target.value)}
                                                style={{
                                                    padding: '8px 10px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                                    color: isDarkMode ? '#f3f4f6' : '#111827',
                                                    fontSize: '14px',
                                                    width: '100%'
                                                }}
                                            />
                                            <select
                                                value={row.is_optional ? 'optional' : 'public'}
                                                onChange={(e) => handleBulkRowChange(index, 'is_optional', e.target.value === 'optional')}
                                                style={{
                                                    padding: '8px 10px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                                    color: isDarkMode ? '#f3f4f6' : '#111827',
                                                    fontSize: '14px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="public">Public</option>
                                                <option value="optional">Optional</option>
                                            </select>
                                            <button
                                                onClick={() => handleRemoveBulkRow(index)}
                                                disabled={bulkHolidayRows.length === 1}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: bulkHolidayRows.length === 1 ? 'not-allowed' : 'pointer',
                                                    padding: '4px',
                                                    color: bulkHolidayRows.length === 1
                                                        ? (isDarkMode ? '#4b5563' : '#d1d5db')
                                                        : (isDarkMode ? '#ef4444' : '#dc2626'),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Add Row Button */}
                                <button
                                    onClick={handleAddBulkRow}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '8px 14px',
                                        marginTop: '12px',
                                        backgroundColor: 'transparent',
                                        color: isDarkMode ? '#60a5fa' : '#3b82f6',
                                        border: `1px dashed ${isDarkMode ? '#60a5fa' : '#3b82f6'}`,
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Plus size={16} />
                                    Add Row
                                </button>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '20px',
                                borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <span style={{
                                    fontSize: '13px',
                                    color: isDarkMode ? '#9ca3af' : '#6b7280'
                                }}>
                                    {bulkHolidayRows.filter(r => r.holiday_name && r.holiday_date).length} valid entries
                                </span>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        onClick={() => setShowBulkAddModal(false)}
                                        disabled={bulkAddLoading}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                                            color: isDarkMode ? '#f3f4f6' : '#111827',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: bulkAddLoading ? 'not-allowed' : 'pointer',
                                            opacity: bulkAddLoading ? 0.5 : 1
                                        }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        onClick={handleSubmitBulkAdd}
                                        disabled={bulkAddLoading || bulkHolidayRows.filter(r => r.holiday_name && r.holiday_date).length === 0}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: (bulkAddLoading || bulkHolidayRows.filter(r => r.holiday_name && r.holiday_date).length === 0)
                                                ? '#9ca3af'
                                                : (isDarkMode ? '#059669' : '#10b981'),
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: (bulkAddLoading || bulkHolidayRows.filter(r => r.holiday_name && r.holiday_date).length === 0)
                                                ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {bulkAddLoading ? 'Adding...' : 'Add Holidays'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Edit Holiday Modal */}
                {showEditHolidayModal && editingHoliday && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: '20px'
                    }}>
                        <div style={{
                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                            borderRadius: '12px', maxWidth: '500px', width: '100%',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }}>
                            <div style={{
                                padding: '20px',
                                borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: isDarkMode ? '#f3f4f6' : '#111827' }}>
                                    Edit Holiday
                                </h2>
                                <button onClick={() => setShowEditHolidayModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#9ca3af' : '#6b7280' }}>
                                    <X size={20} />
                                </button>
                            </div>
                            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: isDarkMode ? '#d1d5db' : '#374151' }}>Holiday Name</label>
                                    <input
                                        type="text"
                                        value={editHolidayName}
                                        onChange={(e) => setEditHolidayName(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                                            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                            color: isDarkMode ? '#f3f4f6' : '#111827', fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: isDarkMode ? '#d1d5db' : '#374151' }}>Date</label>
                                    <input
                                        type="date"
                                        value={editHolidayDate}
                                        onChange={(e) => setEditHolidayDate(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                                            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                            color: isDarkMode ? '#f3f4f6' : '#111827', fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: isDarkMode ? '#d1d5db' : '#374151' }}>Type</label>
                                    <select
                                        value={editHolidayOptional ? 'optional' : 'public'}
                                        onChange={(e) => setEditHolidayOptional(e.target.value === 'optional')}
                                        style={{
                                            width: '100%', padding: '8px 12px', borderRadius: '6px',
                                            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                                            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                            color: isDarkMode ? '#f3f4f6' : '#111827', fontSize: '14px', cursor: 'pointer'
                                        }}
                                    >
                                        <option value="public">Public</option>
                                        <option value="optional">Optional</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{
                                padding: '16px 20px',
                                borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                                display: 'flex', justifyContent: 'flex-end', gap: '12px'
                            }}>
                                <button
                                    onClick={() => setShowEditHolidayModal(false)}
                                    style={{
                                        padding: '8px 16px', backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                                        color: isDarkMode ? '#f3f4f6' : '#111827', border: 'none', borderRadius: '6px',
                                        fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitEditHoliday}
                                    disabled={editHolidayLoading || !editHolidayName || !editHolidayDate}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: (!editHolidayName || !editHolidayDate || editHolidayLoading) ? '#9ca3af' : (isDarkMode ? '#2563eb' : '#3b82f6'),
                                        color: 'white', border: 'none', borderRadius: '6px',
                                        fontSize: '14px', fontWeight: '500',
                                        cursor: (!editHolidayName || !editHolidayDate || editHolidayLoading) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {editHolidayLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default HolidayManagement;